import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  FileText,
  ClipboardCopy,
  Mail,
  RefreshCw,
  MessageSquareText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ProjectTabs from "../components/ProjectTabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Feuille de route — l'ordre de travail, sans calendrier.
 *
 * Un diagramme de Gantt annonce des échéances ; ce n'est pas ce qu'on veut ici.
 * Ce qui est solide, c'est la séquence : chaque jalon dépend de ce qui le
 * précède. Les dates, elles, seraient fausses le lendemain.
 *
 * Chaque jalon pointe vers la section du CDC qu'il vient cocher, pour qu'on
 * puisse toujours répondre à « ça sert à quoi, celui-là ».
 */

const COULEURS = {
  P0: "bg-rose-100 text-rose-700",
  P1: "bg-amber-100 text-amber-700",
  P2: "bg-sky-100 text-sky-700",
  P3: "bg-slate-100 text-slate-600",
};

const STATUTS = {
  a_faire: { libelle: "À faire", puce: "bg-slate-300" },
  en_cours: { libelle: "En cours", puce: "bg-indigo-500" },
  termine: { libelle: "Terminé", puce: "bg-emerald-500" },
  bloque: { libelle: "Bloqué", puce: "bg-red-500" },
};

export default function ProjectRoadmapPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [jalons, setJalons] = useState([]);
  const [synthese, setSynthese] = useState({});
  const [chargement, setChargement] = useState(true);
  const [nouveau, setNouveau] = useState(null);
  const [recapClient, setRecapClient] = useState(null);
  const [recapOuvert, setRecapOuvert] = useState(false);
  const [notesLocales, setNotesLocales] = useState({});
  const [sauvegardeNote, setSauvegardeNote] = useState(null);

  const charger = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/projects/${projectId}/roadmap`, auth);
      const liste = (data.jalons || []).sort((a, b) => (a.ordre || 99) - (b.ordre || 99));
      setJalons(liste);
      setSynthese(data.synthese || {});
      setNotesLocales(Object.fromEntries(liste.map((j) => [j.id, j.note || ""])));
    } catch {
      toast.error("Impossible de charger la feuille de route");
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    charger();
    // eslint-disable-next-line
  }, [projectId]);

  const modifier = async (id, champs) => {
    try {
      await axios.put(`${API_URL}/api/projects/${projectId}/roadmap/${id}`, champs, auth);
      charger();
    } catch {
      toast.error("Modification refusée");
    }
  };

  const initialiserFontaine = async () => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/projects/${projectId}/roadmap/templates/fontaine-btp`,
        {},
        auth
      );
      await charger();
      toast.success(`${data.ajoutes || 0} tâche(s) ajoutée(s), ${data.mis_a_jour || 0} mise(s) à jour`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import Fontaine BTP impossible");
    }
  };

  const genererRecapClient = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/projects/${projectId}/roadmap/client-recap`, auth);
      setRecapClient(data);
      setRecapOuvert(true);
    } catch {
      toast.error("Impossible de générer le récap client");
    }
  };

  const copierRecap = async () => {
    if (!recapClient?.body) return;
    try {
      await navigator.clipboard.writeText(recapClient.body);
      toast.success("Récap client copié");
    } catch {
      toast.error("Copie impossible");
    }
  };

  const ouvrirEmail = () => {
    if (!recapClient?.body) return;
    const subject = encodeURIComponent(recapClient.subject || "Point d'avancement");
    const body = encodeURIComponent(recapClient.body);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const enregistrerNote = async (id) => {
    try {
      setSauvegardeNote(id);
      await axios.put(
        `${API_URL}/api/projects/${projectId}/roadmap/${id}`,
        { note: notesLocales[id] || "" },
        auth
      );
      await charger();
      toast.success("Commentaire enregistré");
    } catch {
      toast.error("Commentaire non enregistré");
    } finally {
      setSauvegardeNote(null);
    }
  };

  // Déplacer un jalon échange son rang avec son voisin : deux écritures, pas
  // une renumérotation complète.
  const deplacer = async (index, sens) => {
    const cible = index + sens;
    if (cible < 0 || cible >= jalons.length) return;
    const a = jalons[index], b = jalons[cible];
    await Promise.all([
      axios.put(`${API_URL}/api/projects/${projectId}/roadmap/${a.id}`, { ordre: b.ordre ?? cible + 1 }, auth),
      axios.put(`${API_URL}/api/projects/${projectId}/roadmap/${b.id}`, { ordre: a.ordre ?? index + 1 }, auth),
    ]);
    charger();
  };

  const supprimer = async (id) => {
    await axios.delete(`${API_URL}/api/projects/${projectId}/roadmap/${id}`, auth);
    charger();
  };

  const creer = async () => {
    if (!nouveau?.titre?.trim()) return;
    try {
      await axios.post(`${API_URL}/api/projects/${projectId}/roadmap`,
        { ...nouveau, ordre: jalons.length + 1 }, auth);
      setNouveau(null);
      charger();
      toast.success("Jalon ajouté");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création refusée");
    }
  };

  if (chargement) {
    return <CRMLayout><div className="flex items-center justify-center py-32 text-slate-400">
      <Loader2 className="w-6 h-6 animate-spin" /></div></CRMLayout>;
  }

  const restants = jalons.filter((j) => j.statut !== "termine").length;

  return (
    <CRMLayout>
      <div className="p-6 space-y-5">
        <Link to="/dashboard/projects" className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Projets
        </Link>
        <ProjectTabs projectId={projectId} actif="roadmap" />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Feuille de route</h1>
            <p className="text-sm text-slate-500">
              {jalons.length} étapes · {synthese.termines || 0} terminées · {restants} restantes
              {synthese.en_retard ? <span className="font-semibold text-red-600"> · {synthese.en_retard} en retard</span> : null}.
              {synthese.source_avancement === "cdc" && " Les lots viennent du CDC : leur avancement suit les fonctionnalités cochées."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={initialiserFontaine}>
              <RefreshCw className="w-4 h-4 mr-1" /> Charger Fontaine BTP
            </Button>
            <Button variant="outline" onClick={genererRecapClient}>
              <MessageSquareText className="w-4 h-4 mr-1" /> Récap client
            </Button>
            <Button onClick={() => setNouveau({ titre: "", priorite: "P1", cdc_section: "" })}>
              <Plus className="w-4 h-4 mr-1" /> Jalon
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all"
              style={{ width: `${synthese.avancement || 0}%` }} />
          </div>
          <span className="text-sm font-bold text-indigo-600 tabular-nums">
            {synthese.avancement || 0}%
          </span>
        </div>

        {nouveau && (
          <div className="bg-white rounded-xl border border-indigo-200 p-4 grid gap-2 md:grid-cols-4">
            <Input className="md:col-span-2" placeholder="Intitulé du jalon"
              value={nouveau.titre} onChange={(e) => setNouveau({ ...nouveau, titre: e.target.value })} />
            <select className="border border-slate-200 rounded-md px-2 text-sm"
              value={nouveau.priorite} onChange={(e) => setNouveau({ ...nouveau, priorite: e.target.value })}>
              {["P0", "P1", "P2", "P3"].map((p) => <option key={p}>{p}</option>)}
            </select>
            <div className="flex gap-2">
              <Input placeholder="Section du CDC" value={nouveau.cdc_section}
                onChange={(e) => setNouveau({ ...nouveau, cdc_section: e.target.value })} />
              <Button onClick={creer}>Ajouter</Button>
            </div>
          </div>
        )}

        {recapOuvert && recapClient && (
          <div className="bg-white rounded-xl border border-indigo-200 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-base font-bold text-slate-900">Récapitulatif client prêt à envoyer</h2>
                <p className="text-xs text-slate-500">
                  Généré depuis les tâches cochées et les commentaires d'avancement.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={copierRecap}>
                  <ClipboardCopy className="w-4 h-4 mr-1" /> Copier
                </Button>
                <Button onClick={ouvrirEmail}>
                  <Mail className="w-4 h-4 mr-1" /> Préparer email
                </Button>
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs font-semibold text-slate-500 mb-2">
                Objet : {recapClient.subject}
              </div>
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">
                {recapClient.body}
              </pre>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {jalons.map((j, i) => {
            const st = STATUTS[j.statut] || STATUTS.a_faire;
            return (
              <div key={j.id} className="px-4 py-3 hover:bg-slate-50/70 group">
                <div className="flex items-start gap-3">
                  <span className="w-7 shrink-0 text-right text-sm font-semibold text-slate-400 tabular-nums pt-0.5">
                    {i + 1}.
                  </span>
                  <span className={`w-2 h-2 rounded-full shrink-0 mt-2 ${st.puce}`} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${COULEURS[j.priorite]}`}>
                        {j.priorite}
                      </span>
                      <span className={`text-sm ${j.statut === "termine"
                        ? "text-slate-400 line-through" : "text-slate-800"}`}>
                        {j.titre}
                      </span>
                      {j.lot && <span className="text-xs text-slate-400">· {j.lot}</span>}
                      {j.source === "cdc" && (
                        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                          {j.avancement || 0}% · {j.fonctionnalites} fonctionnalités
                        </span>
                      )}
                      {j.en_retard && (
                        <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                          En retard · fin prévue {new Date(j.fin).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                    </div>
                    {j.cdc_section && (
                      <button
                        onClick={() => navigate(`/dashboard/projects/${projectId}/cdc`)}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700"
                        title="Ouvrir le cahier des charges"
                      >
                        <FileText className="w-3 h-3" />
                        {j.cdc_section}
                      </button>
                    )}
                  </div>

                  <select
                    value={j.statut}
                    onChange={(e) => modifier(j.id, { statut: e.target.value })}
                    className="text-xs text-slate-500 bg-transparent border border-slate-200 rounded px-1.5 py-1 cursor-pointer"
                  >
                    {Object.entries(STATUTS).map(([k, v]) => (
                      <option key={k} value={k}>{v.libelle}</option>
                    ))}
                  </select>

                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                    <button onClick={() => deplacer(i, -1)} disabled={i === 0}
                      className="text-slate-300 hover:text-slate-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => deplacer(i, 1)} disabled={i === jalons.length - 1}
                      className="text-slate-300 hover:text-slate-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => supprimer(j.id)}
                      className="text-slate-300 hover:text-red-500 ml-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="mt-3 ml-12 grid gap-2 md:grid-cols-[1fr_auto]">
                  <Textarea
                    rows={2}
                    placeholder="Commentaire d'avancement interne ou résumé client..."
                    value={notesLocales[j.id] ?? ""}
                    onChange={(e) => setNotesLocales({ ...notesLocales, [j.id]: e.target.value })}
                    className="text-sm bg-white"
                  />
                  <Button
                    variant="outline"
                    onClick={() => enregistrerNote(j.id)}
                    disabled={sauvegardeNote === j.id}
                  >
                    {sauvegardeNote === j.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    Commenter
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400">
          Chaque jalon renvoie à la section du CDC qu'il vient cocher. Les flèches
          réordonnent la séquence — c'est elle qui porte les dépendances.
        </p>
      </div>
    </CRMLayout>
  );
}
